import {
  all,
  call,
  delay,
  put,
  race,
  select,
  takeLatest,
} from 'redux-saga/effects';

import { get, post, putRequest } from '../../../utils/request';
import makeSelectMobile from '../../Mobile/selectors';
import { changeStep, nachaSaveToken } from '../OneTimePayment/actions';
import { changeStep as autopayChangeStep } from '../Autopay/actions';
import makeSelectMain from '../../Main/selectors';
import {
  nachaValidationFailure,
  setInputError,
  setIsSourceDrawerOpen,
  validateRoutingFailure,
  validateRoutingSuccess,
} from './actions';
import {
  DEFAULT_ERROR_MESSAGE,
  NACHA_DICTIONARY,
  VALIDATE_ROUTING,
} from './constants';

export function* nachaStatusSaga({
  baseUrl,
  loanNumber,
  navigationHandler,
  verificationToken,
}) {
  const isMobile = yield select(makeSelectMobile('isMobile'));
  try {
    const endpoint = `${baseUrl}/paymentMethods/${loanNumber}/status`;
    while (true) {
      let finalToken = null;
      let verificationStatus = null;
      if (process.env.APP === 'csr') {
        ({ finalToken, verificationStatus } = {
          finalToken: 'Csr final token',
          verificationStatus: 'Valid',
        });
      } else {
        ({ finalToken, verificationStatus } = yield call(
          putRequest, endpoint, { verificationToken }
        ));
      }

      if (verificationStatus === 'InProcess') {
        yield delay(10000);
      } else if (NACHA_DICTIONARY[verificationStatus]) {
        yield put(nachaSaveToken(finalToken));
        yield put(validateRoutingSuccess(true));
        if (isMobile) {
          navigationHandler('SchedulePayment');
        } else {
          yield put(changeStep(1));
          yield put(setIsSourceDrawerOpen(false));
        }
        break;
      } else {
        yield put(nachaValidationFailure(DEFAULT_ERROR_MESSAGE));
        if (isMobile) {
          navigationHandler('PaymentSourceForm');
        } else {
          yield put(changeStep(1));
        }
        break;
      }
    }
  } catch {
    yield put(nachaValidationFailure(DEFAULT_ERROR_MESSAGE));
    if (isMobile) {
      navigationHandler('PaymentSourceForm');
    } else {
      yield put(changeStep(1));
    }
  }
}

export function* nachaVerifySaga({ baseUrl, navigationHandler, payload }) {
  const loanNumber = yield select(makeSelectMain('selectedLoan'));
  const isMobile = yield select(makeSelectMobile('isMobile'));
  try {
    const {
      accountNumber,
      accountType,
      nameOnAccount,
      routingNumber,
    } = payload;
    const endpoint = `${baseUrl}/paymentMethods/${loanNumber}/verify`;
    let [verificationStatus, verificationToken] = [null, null];
    if (process.env.APP === 'csr') {
      [verificationStatus, verificationToken] = ['InProcess', 'Csr token'];
    } else {
      [{ verificationStatus, verificationToken }] = yield all([
        call(post, endpoint, {
          accountNumber,
          accountType,
          nameOnAccount,
          routingNumber,
        }),
        delay(5000),
      ]);
    }

    if (verificationStatus === 'InProcess') {
      yield call(nachaStatusSaga, {
        baseUrl,
        loanNumber,
        navigationHandler,
        verificationToken,
      });
    } else if (NACHA_DICTIONARY[verificationStatus]) {
      yield put(nachaSaveToken(verificationToken));
      yield put(validateRoutingSuccess(true));
      if (isMobile) {
        navigationHandler('SchedulePayment');
      } else {
        yield put(changeStep(1));
      }
    } else {
      yield put(nachaValidationFailure(DEFAULT_ERROR_MESSAGE));
      if (isMobile) {
        navigationHandler('PaymentSourceForm');
      } else {
        yield put(changeStep(1));
      }
    }
  } catch {
    yield put(nachaValidationFailure(DEFAULT_ERROR_MESSAGE));
    if (isMobile) {
      navigationHandler('PaymentSourceForm');
    } else {
      yield put(changeStep(1));
    }
  }
}

export function* validateRoutingSaga({ baseUrl, navigationHandler }, { payload }) {
  try {
    const isMobile = yield select(makeSelectMobile('isMobile'));

    const bankName = process.env.APP === 'csr' 
      ? 'csr' 
      : (yield call(get, `${baseUrl}/utilities/${payload.routingNumber}`)).bankName;

    const { isOtp } = payload;

    if (!bankName) {
      yield put(setInputError({ field: 'routingNumber', value: 'Routing Number is invalid' }));
      yield put(validateRoutingSuccess(false));
    } else if (isOtp) {
      if (isMobile) {
        navigationHandler('Nacha');
      } else {
        yield put(changeStep(6));
      }
      const { timeout } = yield race({
        nacha: call(nachaVerifySaga, { baseUrl, navigationHandler, payload }),
        timeout: delay(30000),
      });
      if (timeout) {
        yield put(validateRoutingSuccess(true));
        if (isMobile) {
          navigationHandler('SchedulePayment');
        } else {
          yield put(changeStep(1));
        }
      }
    } else {
      yield put(validateRoutingSuccess(true));
      if (isMobile) {
        navigationHandler('AutopaySetupOverview');
      } else {
        yield put(autopayChangeStep(1));
        yield put(setIsSourceDrawerOpen(false));
      }
    }
  } catch {
    yield put(validateRoutingFailure());
  }
}

export default (options) => function* watcherSaga() {
  yield takeLatest(VALIDATE_ROUTING, validateRoutingSaga, options);
};
